import type { ServerStatus } from '@/services/media-upload.service'
import { HugeiconsIcon } from '@hugeicons/react'
import {
  AlertCircleIcon,
  Cancel01Icon,
  CheckmarkCircle01Icon,
  Loading03Icon
} from '@hugeicons/core-free-icons'
import { useTranslation } from 'react-i18next'

export default function BlossomUploadChecklist({
  fileName,
  servers,
  onCancel
}: {
  fileName: string
  servers: { url: string; status: ServerStatus }[]
  onCancel?: () => void
}) {
  const { t } = useTranslation()
  return (
    <div className="rounded-lg border bg-muted/30 p-3">
      <div className="mb-2 flex items-center justify-between gap-2">
        <div className="truncate text-xs font-medium text-foreground">{fileName}</div>
        {onCancel && (
          <button
            type="button"
            onClick={onCancel}
            className="text-muted-foreground hover:text-foreground"
            title={t('Cancel')}
          >
            <HugeiconsIcon icon={Cancel01Icon} className="h-3.5 w-3.5" />
          </button>
        )}
      </div>
      {servers.length === 0 ? (
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <HugeiconsIcon icon={Loading03Icon} className="h-3 w-3 animate-spin" />
          <span>{t('Loading Blossom servers...')}</span>
        </div>
      ) : (
        <ul className="space-y-1.5">
          {servers.map(({ url, status }) => (
            <ServerRow key={url} url={url} status={status} />
          ))}
        </ul>
      )}
    </div>
  )
}

function ServerRow({ url, status }: { url: string; status: ServerStatus }) {
  const hostname = (() => {
    try {
      return new URL(url).hostname
    } catch {
      return url
    }
  })()

  return (
    <li className="text-xs">
      <div className="flex items-center gap-2">
        <StatusIcon status={status} />
        <span className="min-w-0 flex-1 truncate text-muted-foreground" title={url}>
          {hostname}
        </span>
        <div className="w-20">
          <StatusBar status={status} />
        </div>
      </div>
      {status.state === 'error' && status.error && (
        <div className="ml-5 mt-0.5 truncate text-[10px] text-destructive" title={status.error}>
          {status.error}
        </div>
      )}
    </li>
  )
}

function StatusIcon({ status }: { status: ServerStatus }) {
  if (status.state === 'uploading') {
    return <HugeiconsIcon icon={Loading03Icon} className="h-3.5 w-3.5 animate-spin text-primary" />
  }
  if (status.state === 'success') {
    return <HugeiconsIcon icon={CheckmarkCircle01Icon} className="h-3.5 w-3.5 text-green-500" />
  }
  if (status.state === 'error') {
    return <HugeiconsIcon icon={AlertCircleIcon} className="h-3.5 w-3.5 text-destructive" />
  }
  return <span className="block h-2 w-2 rounded-full bg-muted-foreground/40" />
}

function StatusBar({ status }: { status: ServerStatus }) {
  if (status.state === 'pending') {
    return <div className="h-1 w-full rounded-full bg-muted" />
  }
  if (status.state === 'uploading') {
    return (
      <div className="relative h-1 w-full overflow-hidden rounded-full bg-muted">
        <div className="absolute inset-y-0 left-0 w-1/2 animate-pulse bg-primary" />
      </div>
    )
  }
  if (status.state === 'success') {
    return <div className="h-1 w-full rounded-full bg-green-500" />
  }
  return <div className="h-1 w-full rounded-full bg-destructive" />
}
